import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Stack,
} from '@mui/material';
import { Plus as PlusIcon, Trash2 as TrashIcon, GripVertical as GripIcon } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useRoutineStore } from '../store/routineStore';
import { useExerciseStore } from '../store/exerciseStore';
import RoutineManagement from '../components/RoutineManagement';

const Routines = ({ onStartWorkout }) => {
  const createRoutine = useRoutineStore((state) => state.createRoutine);
  const updateRoutine = useRoutineStore((state) => state.updateRoutine);
  const addExercise = useExerciseStore((state) => state.addExercise);
  const exercises = useExerciseStore((state) => state.exercises);

  const [createRoutineDialogOpen, setCreateRoutineDialogOpen] = useState(false);
  const [addNewExerciseDialogOpen, setAddNewExerciseDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [routineName, setRoutineName] = useState('');
  const [dayName, setDayName] = useState('');
  const [selectedExercises, setSelectedExercises] = useState([]);

  // Estado para nuevo ejercicio
  const [newExercise, setNewExercise] = useState({
    name: '',
    muscleGroup: 'pecho',
    type: 'push',
    defaultSets: 3,
    defaultReps: '8-12',
    defaultWeight: 0,
    notes: '',
  });

  const handleCreateRoutine = () => {
    if (routineName.trim()) {
      createRoutine({
        name: routineName,
        days: [
          {
            id: 'day1',
            name: 'Día 1',
            exercises: [],
          },
        ],
      });
      setRoutineName('');
      setCreateRoutineDialogOpen(false);
    }
  };

  const handleEditDay = (routine, day) => {
    setSelectedRoutine(routine);
    setSelectedDay(day);
    setDayName(day.name);
    setSelectedExercises(day.exercises.map((e) => e.exerciseId));
    setEditDialogOpen(true);
  };

  const handleSaveDay = () => {
    if (selectedRoutine && selectedDay) {
      const updatedRoutine = {
        ...selectedRoutine,
        days: selectedRoutine.days.map((d) => {
          if (d.id === selectedDay.id) {
            return {
              ...d,
              name: dayName,
              exercises: selectedExercises.map((exId, idx) => {
                const exercise = exercises.find((e) => e.id === exId);
                return {
                  exerciseId: exId,
                  sets: exercise?.defaultSets || 3,
                  reps: exercise?.defaultReps || '8-10',
                  order: idx + 1,
                  name: exercise?.name || '',
                };
              }),
            };
          }
          return d;
        }),
      };
      updateRoutine(selectedRoutine.id, updatedRoutine);
      setEditDialogOpen(false);
      setSelectedRoutine(null);
      setSelectedDay(null);
    }
  };

  const toggleExercise = (exerciseId) => {
    setSelectedExercises((prev) => {
      if (prev.includes(exerciseId)) {
        return prev.filter((id) => id !== exerciseId);
      } else {
        return [...prev, exerciseId];
      }
    });
  };

  const handleAddNewExercise = () => {
    if (newExercise.name.trim()) {
      addExercise({
        name: newExercise.name,
        muscleGroup: newExercise.muscleGroup,
        type: newExercise.type,
        defaultSets: parseInt(newExercise.defaultSets) || 3,
        defaultReps: newExercise.defaultReps,
        defaultWeight: parseInt(newExercise.defaultWeight) || 0,
        notes: newExercise.notes,
      });
      setNewExercise({
        name: '',
        muscleGroup: 'pecho',
        type: 'push',
        defaultSets: 3,
        defaultReps: '8-12',
        defaultWeight: 0,
        notes: '',
      });
      setAddNewExerciseDialogOpen(false);
    }
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // Reordenar ejercicios dentro del mismo día
    if (source.droppableId === 'exercises-list') {
      const newExercises = Array.from(selectedExercises);
      const [movedExercise] = newExercises.splice(source.index, 1);
      newExercises.splice(destination.index, 0, movedExercise);
      setSelectedExercises(newExercises);
    }
  };

  const getMuscleGroupColor = (group) => {
    const colors = {
      pecho: '#FF6B6B',
      espalda: '#4ECDC4',
      piernas: '#45B7D1',
      brazos: '#FFA07A',
      hombros: '#FFD93D',
      core: '#DDA15E',
    };
    return colors[group] || '#95E1D3';
  };

  const muscleGroups = ['pecho', 'espalda', 'piernas', 'brazos', 'hombros', 'core'];
  const exerciseTypes = ['push', 'pull', 'legs', 'core'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Mis Rutinas
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PlusIcon size={18} />}
            onClick={() => setAddNewExerciseDialogOpen(true)}
          >
            Agregar Ejercicio
          </Button>
          <Button
            variant="contained"
            startIcon={<PlusIcon size={18} />}
            onClick={() => setCreateRoutineDialogOpen(true)}
          >
            Nueva Rutina
          </Button>
        </Stack>
      </Box>

      <RoutineManagement onEdit={handleEditDay} />

      {/* Create Routine Dialog */}
      <Dialog
        open={createRoutineDialogOpen}
        onClose={() => {
          setCreateRoutineDialogOpen(false);
          setRoutineName('');
        }}
      >
        <DialogTitle>Nueva Rutina</DialogTitle>
        <DialogContent sx={{ minWidth: 300, pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Nombre de la rutina"
            value={routineName}
            onChange={(e) => setRoutineName(e.target.value)}
            placeholder="Ej: Push/Pull, PPL, Upper/Lower"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateRoutineDialogOpen(false);
              setRoutineName('');
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateRoutine}
            variant="contained"
            disabled={!routineName.trim()}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Exercise Dialog */}
      <Dialog
        open={addNewExerciseDialogOpen}
        onClose={() => {
          setAddNewExerciseDialogOpen(false);
          setNewExercise({
            name: '',
            muscleGroup: 'pecho',
            type: 'push',
            defaultSets: 3,
            defaultReps: '8-12',
            defaultWeight: 0,
            notes: '',
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Agregar Nuevo Ejercicio</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              autoFocus
              fullWidth
              label="Nombre del ejercicio"
              value={newExercise.name}
              onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
              placeholder="Ej: Sentadilla, Press de Banca"
            />

            <FormControl fullWidth>
              <InputLabel>Grupo muscular</InputLabel>
              <Select
                value={newExercise.muscleGroup}
                onChange={(e) => setNewExercise({ ...newExercise, muscleGroup: e.target.value })}
                label="Grupo muscular"
              >
                {muscleGroups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group.charAt(0).toUpperCase() + group.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={newExercise.type}
                onChange={(e) => setNewExercise({ ...newExercise, type: e.target.value })}
                label="Tipo"
              >
                {exerciseTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={2}>
              <TextField
                type="number"
                label="Series"
                value={newExercise.defaultSets}
                onChange={(e) => setNewExercise({ ...newExercise, defaultSets: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Reps"
                value={newExercise.defaultReps}
                onChange={(e) => setNewExercise({ ...newExercise, defaultReps: e.target.value })}
                placeholder="Ej: 8-12"
                sx={{ flex: 1 }}
              />
            </Stack>

            <TextField
              type="number"
              label="Peso por defecto (kg)"
              value={newExercise.defaultWeight}
              onChange={(e) => setNewExercise({ ...newExercise, defaultWeight: e.target.value })}
            />

            <TextField
              fullWidth
              label="Notas"
              value={newExercise.notes}
              onChange={(e) => setNewExercise({ ...newExercise, notes: e.target.value })}
              multiline
              rows={2}
              placeholder="Ej: Con mancuernas, en máquina, etc."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddNewExerciseDialogOpen(false);
              setNewExercise({
                name: '',
                muscleGroup: 'pecho',
                type: 'push',
                defaultSets: 3,
                defaultReps: '8-12',
                defaultWeight: 0,
                notes: '',
              });
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddNewExercise}
            variant="contained"
            disabled={!newExercise.name.trim()}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Day Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedRoutine(null);
          setSelectedDay(null);
          setSelectedExercises([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Día</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Nombre del día"
            value={dayName}
            onChange={(e) => setDayName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Selecciona Ejercicios (Arrastra para reordenar)
          </Typography>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="exercises-list">
              {(provided, snapshot) => (
                <Box
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                    p: 1,
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {Array.from(selectedExercises).map((exerciseId, index) => {
                    const exercise = exercises.find((e) => e.id === exerciseId);
                    return (
                      <Draggable key={exerciseId} draggableId={exerciseId} index={index}>
                        {(provided, snapshot) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              p: 1,
                              mb: 0.5,
                              bgcolor: snapshot.isDragging ? 'primary.light' : 'grey.50',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              cursor: 'grab',
                              transition: 'all 0.2s ease',
                              '&:active': {
                                cursor: 'grabbing',
                              },
                            }}
                          >
                            <GripIcon size={18} sx={{ color: 'primary.main', flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {index + 1}. {exercise?.name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Chip
                                  label={exercise?.muscleGroup}
                                  size="small"
                                  sx={{
                                    bgcolor: getMuscleGroupColor(exercise?.muscleGroup),
                                    color: 'white',
                                    height: 20,
                                    fontSize: '0.7rem',
                                  }}
                                />
                                <Chip
                                  label={`${exercise?.defaultSets}x${exercise?.defaultReps}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => {
                                const newExercises = selectedExercises.filter((id) => id !== exerciseId);
                                setSelectedExercises(newExercises);
                              }}
                            >
                              <TrashIcon size={16} />
                            </Button>
                          </Paper>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Agregar Ejercicios
          </Typography>

          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {exercises
              .filter((ex) => !selectedExercises.includes(ex.id))
              .map((exercise) => (
                <Box
                  key={exercise.id}
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                  }}
                >
                  <Checkbox
                    checked={selectedExercises.includes(exercise.id)}
                    onChange={() => toggleExercise(exercise.id)}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {exercise.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip
                        label={exercise.muscleGroup}
                        size="small"
                        sx={{
                          bgcolor: getMuscleGroupColor(exercise.muscleGroup),
                          color: 'white',
                          height: 20,
                          fontSize: '0.7rem',
                        }}
                      />
                      <Chip
                        label={`${exercise.defaultSets}x${exercise.defaultReps}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setSelectedRoutine(null);
              setSelectedDay(null);
              setSelectedExercises([]);
            }}
          >
            Cancelar
          </Button>
          <Button onClick={handleSaveDay} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Routines;
